import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { CatalogImportRequest } from '@/api/handlers/resources/catalogImports';
import type { ErrorResponse } from '@/api/handlers/responses';
import {
    useAllBrandsQuery,
    useAttributeTypesQuery,
    useCatalogImportPreviewMutation,
    useCategoryPickerQuery,
    useSupplierPickerQuery,
} from '@/api/queries';
import {
    buildCatalogImportRequest,
    useCatalogImportParser,
} from '@/pages/catalogImport/parse';
import { useCatalogImport } from '@/pages/catalogImport/store';
import {
    buildCatalogImportTemplate,
    resolveTemplateLanguage,
    templateFilename,
    type CatalogImportTemplateInput,
} from '@/pages/catalogImport/template';
import { useImportWork, type ImportWork } from '@/pages/catalogImport/work';
import { downloadBlob, xlsxBlobOf } from '@/utils/fileDownload';
import {
    droppedFiles,
    intakeOf,
    type DroppedItem,
    type FileRefusal,
} from './uploadIntake';
import {
    blockOfRefusal,
    hasWarnings,
    uploadIntakeState,
    warningsOf,
    type UploadBlock,
    type UploadIntakeState,
    type UploadRun,
} from './uploadState';

export type UseUploadStepOptions = {
    /** Where Back goes: the step before Upload. */
    backPath: string;
    /** Where Continue goes, and where a clean first check leads: Names. */
    nextPath: string;
};

export type UseUploadStepResult = {
    /** The store names the branch the file is for; without one the step cannot run. */
    hasBranch: boolean;
    /** From the press until the template is handed over or fails. */
    isPreparingTemplate: boolean;
    /** The last template press failed; the next press is the retry. */
    templateFailed: boolean;
    onDownloadTemplate: () => void;
    intake: UploadIntakeState;
    /** The owner's work on the stored file (`importWorkOf`): the card counts it. */
    work: ImportWork;
    /**
     * A file picked or dropped (on the drop zone, or over the read file's
     * card). Over a file with work it asks first (`replaceConfirm`).
     */
    onFilesPicked: (files: readonly File[]) => void;
    onDrop: (event: { items: DroppedItem[] }) => void;
    /**
     * Cancel while the file is read or checked, and Other file once it is:
     * drops the file and shows the drop zone again. A stored file with work
     * asks first (`replaceConfirm`).
     */
    onDiscardFile: () => void;
    /**
     * Replacing the file is asked, with the work it would lose: open from
     * Other file (or Cancel) or a new file on a file with work until the
     * owner answers. `null` when nothing is asked.
     */
    replaceConfirm: { work: ImportWork } | null;
    /** Keep this file: closes the question and forgets the new file. */
    onKeepFile: () => void;
    /** Replace file: drops the file, or reads the new one from scratch. */
    onConfirmReplace: () => void;
    /** Runs the check again after it did not answer. */
    onRetry: () => void;
    onBack: () => void;
    /** Goes on to Names; only offered once the file is checked. */
    onContinue: () => void;
};

/** The catalogue lists the template is built from, everything but the language. */
export type TemplateLists = Omit<CatalogImportTemplateInput, 'language'>;

/** A query as this hook reads it: its data now, or a fetch for it. */
type ReadableQuery<T> = {
    data: T | undefined;
    refetch: () => Promise<{ data?: T | undefined }>;
};

/**
 * The data a query holds when `isUsable` accepts it, else a fresh fetch's.
 * `undefined` when neither has it: the template is not built from a partial
 * catalogue.
 */
const readQuery = async <T>(
    query: ReadableQuery<T>,
    isUsable: (data: T) => boolean,
): Promise<T | undefined> => {
    if (query.data !== undefined && isUsable(query.data)) {
        return query.data;
    }
    const { data } = await query.refetch();
    return data !== undefined && isUsable(data) ? data : undefined;
};

const isSuccessEnvelope = (data: { success: boolean }): boolean => data.success;
const isAnything = (): boolean => true;

/** What a confirmed Replace file does: drop the file, or read the new one. */
type PendingReplace = { kind: 'discard' } | { kind: 'read'; file: File };

/** The first preview's decisions: none yet, the Names step makes them. */
const NO_DECISIONS: CatalogImportRequest['names'] = {
    brands: [],
    categories: [],
    suppliers: [],
};

/**
 * Upload: the template download, the drop zone and the file's first check,
 * shared by the wizard and the in-shell import page.
 *
 * - **Template.** The step reads the category and supplier pickers, every
 *   brand and the attribute dictionary as it mounts, so a press usually
 *   builds at once; a list that has not arrived, or failed, is fetched on the
 *   press. The workbook is written in the UI language at the time of the
 *   press and saved as `templateFilename(language)`. One build at a time; a
 *   failure leaves a flag the View turns into an alert.
 * - **File.** A drop or a pick goes through `intakeOf`: one `.xlsx` file is
 *   accepted, anything else is refused with its reason and the drop zone
 *   stays. An accepted file is parsed in the Web Worker
 *   (`useCatalogImportParser`) with every attribute type's name; a refusal
 *   stops it before the store. The store knows a read runs
 *   (`isReadingFile`), so the step strip offers no links meanwhile. A parsed file goes to the store (`setFile`
 *   with its hash, row count and ignored columns, then `setLines`) and is
 *   previewed at once with no decisions. A clean answer (no header problem,
 *   no warning) leads straight on to Names; otherwise the step shows the
 *   warnings with Continue, or the blocking notice.
 * - **Coming back.** A file already in the store shows as it stands; one
 *   whose preview was dropped (another branch was chosen) is checked again.
 *
 * Cancel and Other file drop the file, and an answer for a file that was
 * dropped or replaced is ignored. Leaving the step mid-check still stores the
 * answer (the store outlives the step) but goes nowhere.
 *
 * **Replacing a file with work asks first.** While the store holds a file
 * the owner worked on (`importWorkOf`: edited rows, excluded rows, changed
 * name choices), Other file (or Cancel) and a new file picked or dropped
 * open `replaceConfirm` instead, holding the new file; Replace file drops
 * the file or reads the new one from scratch, Keep this file forgets it.
 * Without work both act at once. A file being read has no work yet, so its
 * Cancel never asks.
 */
export const useUploadStep = ({
    backPath,
    nextPath,
}: UseUploadStepOptions): UseUploadStepResult => {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const { state, actions } = useCatalogImport();
    const parser = useCatalogImportParser();
    const { mutateAsync: requestPreview } = useCatalogImportPreviewMutation();
    const categoriesQuery = useCategoryPickerQuery();
    const suppliersQuery = useSupplierPickerQuery();
    const brandsQuery = useAllBrandsQuery();
    const attributeTypesQuery = useAttributeTypesQuery();

    const [isPreparingTemplate, setIsPreparingTemplate] = React.useState(false);
    const [templateFailed, setTemplateFailed] = React.useState(false);
    const isPreparing = React.useRef(false);

    const [refusal, setRefusal] = React.useState<FileRefusal | null>(null);
    const [run, setRun] = React.useState<UploadRun | null>(null);
    const [previewError, setPreviewError] =
        React.useState<ErrorResponse | null>(null);
    // Bumped by every new file, Cancel and Other file: an answer that arrives
    // for an earlier one is dropped.
    const generation = React.useRef(0);
    const isMounted = React.useRef(true);

    React.useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const readTemplateLists =
        React.useCallback(async (): Promise<TemplateLists | null> => {
            const [categories, suppliers, brands, attributeTypes] =
                await Promise.all([
                    readQuery(categoriesQuery, isSuccessEnvelope),
                    readQuery(suppliersQuery, isSuccessEnvelope),
                    readQuery(brandsQuery, isAnything),
                    readQuery(attributeTypesQuery, isSuccessEnvelope),
                ]);
            if (
                !categories?.success ||
                !suppliers?.success ||
                !brands ||
                !attributeTypes?.success
            ) {
                return null;
            }
            return {
                categories: categories.response.items,
                suppliers: suppliers.response.items.map(({ name }) => ({
                    name,
                })),
                brands: brands.map(({ name }) => ({ name })),
                attributeTypes: attributeTypes.response.items,
            };
        }, [attributeTypesQuery, brandsQuery, categoriesQuery, suppliersQuery]);

    const onDownloadTemplate = React.useCallback(() => {
        if (isPreparing.current) {
            return;
        }
        isPreparing.current = true;
        setIsPreparingTemplate(true);
        setTemplateFailed(false);
        const language = resolveTemplateLanguage(i18n.language);

        void (async () => {
            try {
                const lists = await readTemplateLists();
                if (!lists) {
                    throw new Error('The catalogue lists did not load.');
                }
                const { workbook } = await buildCatalogImportTemplate({
                    language,
                    ...lists,
                });
                downloadBlob(
                    await xlsxBlobOf(workbook),
                    templateFilename(language),
                );
            } catch (error) {
                // The console line is for whoever is debugging (the app has
                // no diagnostic adapter yet); the flag is for whoever pressed.
                console.error(
                    '[catalog import] the template failed to build:',
                    error,
                );
                setTemplateFailed(true);
            } finally {
                isPreparing.current = false;
                setIsPreparingTemplate(false);
            }
        })();
    }, [i18n.language, readTemplateLists]);

    /**
     * Previews `request` and stores the answer. `leadOn` (a freshly parsed
     * file) goes on to Names when the answer needs nothing pointed out.
     */
    const runPreview = React.useCallback(
        async (
            id: number,
            request: CatalogImportRequest,
            {
                ignoredColumns,
                leadOn,
            }: { ignoredColumns: string[]; leadOn: boolean },
        ) => {
            setPreviewError(null);
            const revision = actions.previewStarted();
            try {
                const preview = await requestPreview(request);
                // The generation is this step's (a new file, Cancel, Other
                // file); the revision is the store's, which outlives the
                // step: a check sent from an earlier visit is dropped too.
                if (
                    generation.current !== id ||
                    !actions.setPreview(preview, revision)
                ) {
                    return;
                }
                const isClear =
                    preview.header.problems.length === 0 &&
                    !hasWarnings(warningsOf({ ignoredColumns }, preview));
                if (leadOn && isClear && isMounted.current) {
                    navigate(nextPath);
                }
            } catch (error) {
                if (
                    generation.current !== id ||
                    !actions.previewFailed(error, revision)
                ) {
                    return;
                }
                setPreviewError(error as ErrorResponse);
            }
        },
        [actions, navigate, nextPath, requestPreview],
    );

    const branchId = state.branch?.id ?? '';

    /**
     * Parses `file` in the worker, stores it and runs its first preview. The
     * store knows the read runs (`isReadingFile`) until the file reaches it
     * or the read ends another way.
     */
    const readFile = React.useCallback(
        async (file: File) => {
            generation.current += 1;
            const id = generation.current;
            actions.setFile(null);
            actions.fileReadStarted();
            setPreviewError(null);
            setRun({ phase: 'reading', file });
            // The file reaching the store (`setFile`) ends the read; every
            // other way out (a stop, a refusal, a cancel, an unmount) ends it
            // here, unless a newer read or Cancel has taken it over.
            try {
                const stopWith = (block: UploadBlock) => {
                    if (generation.current === id) {
                        setRun({ phase: 'stopped', file, block });
                    }
                };

                const attributeTypes = await readQuery(
                    attributeTypesQuery,
                    isSuccessEnvelope,
                );
                if (generation.current !== id) {
                    return;
                }
                if (!attributeTypes?.success) {
                    stopWith({ kind: 'checkFailed' });
                    return;
                }

                let buffer: ArrayBuffer;
                try {
                    buffer = await file.arrayBuffer();
                } catch {
                    stopWith({ kind: 'unreadable' });
                    return;
                }
                if (generation.current !== id) {
                    return;
                }

                const outcome = await parser.start({
                    buffer,
                    attributeTypeNames: attributeTypes.response.items.map(
                        ({ name }) => name,
                    ),
                });
                if (generation.current !== id) {
                    return;
                }
                switch (outcome.status) {
                    case 'cancelled':
                        return;
                    case 'failed':
                        stopWith({ kind: 'unreadable' });
                        return;
                    case 'refused':
                        stopWith(
                            blockOfRefusal(
                                outcome.refusal,
                                resolveTemplateLanguage(i18n.language),
                            ),
                        );
                        return;
                    case 'done':
                        break;
                }

                const { lines, ignoredColumns, rowCount, hash } =
                    outcome.parsed;
                if (lines.length === 0) {
                    stopWith({ kind: 'empty' });
                    return;
                }
                actions.setFile({
                    name: file.name,
                    hash,
                    rowCount,
                    ignoredColumns,
                });
                actions.setLines(lines);
                setRun(null);
                await runPreview(
                    id,
                    buildCatalogImportRequest({
                        branchId,
                        file: { name: file.name, hash },
                        lines,
                        decisions: NO_DECISIONS,
                        exclusions: new Set(),
                    }),
                    { ignoredColumns, leadOn: true },
                );
            } finally {
                if (generation.current === id) {
                    actions.fileReadEnded();
                }
            }
        },
        [
            actions,
            attributeTypesQuery,
            branchId,
            i18n.language,
            parser,
            runPreview,
        ],
    );

    /** The stored file's preview again, with everything the store holds. */
    const recheck = React.useCallback(() => {
        if (!state.file) {
            return;
        }
        generation.current += 1;
        void runPreview(
            generation.current,
            buildCatalogImportRequest({
                branchId,
                file: state.file,
                lines: state.lines,
                decisions: state.decisions,
                exclusions: state.exclusions,
            }),
            { ignoredColumns: state.file.ignoredColumns, leadOn: false },
        );
    }, [
        branchId,
        runPreview,
        state.decisions,
        state.exclusions,
        state.file,
        state.lines,
    ]);

    // A stored file whose preview was dropped (Branch chose again) is checked
    // again as the step opens.
    const needsRecheck =
        run === null && state.file !== null && state.previewStatus === 'idle';
    React.useEffect(() => {
        if (needsRecheck) {
            recheck();
        }
    }, [needsRecheck, recheck]);

    // Only a stored file can carry work; a file being read has none yet.
    const {
        work,
        asksFirst: asksBeforeReplacing,
        pending: pendingReplace,
        setPending: setPendingReplace,
    } = useImportWork<PendingReplace>();

    const onFilesPicked = React.useCallback(
        (files: readonly File[]) => {
            const intake = intakeOf(files);
            if (intake.kind === 'none') {
                return;
            }
            if (intake.kind === 'refused') {
                setRefusal(intake.refusal);
                return;
            }
            setRefusal(null);
            if (asksBeforeReplacing) {
                setPendingReplace({ kind: 'read', file: intake.file });
                return;
            }
            void readFile(intake.file);
        },
        [asksBeforeReplacing, readFile, setPendingReplace],
    );

    const onDrop = React.useCallback(
        (event: { items: DroppedItem[] }) => {
            void droppedFiles(event.items).then(onFilesPicked);
        },
        [onFilesPicked],
    );

    const discardFile = React.useCallback(() => {
        generation.current += 1;
        parser.cancel();
        setRun(null);
        setPreviewError(null);
        setRefusal(null);
        actions.setFile(null);
    }, [actions, parser]);

    const onDiscardFile = React.useCallback(() => {
        if (asksBeforeReplacing) {
            setPendingReplace({ kind: 'discard' });
            return;
        }
        discardFile();
    }, [asksBeforeReplacing, discardFile, setPendingReplace]);

    const onKeepFile = React.useCallback(
        () => setPendingReplace(null),
        [setPendingReplace],
    );

    const onConfirmReplace = React.useCallback(() => {
        setPendingReplace(null);
        if (pendingReplace?.kind === 'read') {
            void readFile(pendingReplace.file);
            return;
        }
        discardFile();
    }, [discardFile, pendingReplace, readFile, setPendingReplace]);

    const onRetry = React.useCallback(() => {
        if (run?.phase === 'stopped') {
            void readFile(run.file);
            return;
        }
        recheck();
    }, [readFile, recheck, run]);

    const onBack = React.useCallback(
        () => navigate(backPath),
        [backPath, navigate],
    );

    const onContinue = React.useCallback(
        () => navigate(nextPath),
        [navigate, nextPath],
    );

    return {
        hasBranch: state.branch !== null,
        isPreparingTemplate,
        templateFailed,
        onDownloadTemplate,
        intake: uploadIntakeState({
            run,
            refusal,
            progress: parser.progress,
            file: state.file,
            preview: state.preview,
            previewStatus: state.previewStatus,
            previewError,
        }),
        work,
        onFilesPicked,
        onDrop,
        onDiscardFile,
        replaceConfirm: pendingReplace === null ? null : { work },
        onKeepFile,
        onConfirmReplace,
        onRetry,
        onBack,
        onContinue,
    };
};
